import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import useAuthGuard from '../../hooks/useAuthGuard';
import useAuthStore from '../../store/auth';
import api from '../../services/api';
import { User, Role, Status, AdminFilters } from '../../types';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const AdminsPage = () => {
  useAuthGuard(['super_admin']);
  const navigate = useNavigate();
  const currentUser = useAuthStore((state) => state.user);

  const [admins, setAdmins] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<AdminFilters>({});
  const [totalRows, setTotalRows] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  // Password modal state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState<User | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

    const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admins', {
        params: {
          ...filters,
          page: paginationModel.page + 1,
          limit: paginationModel.pageSize,
        },
      });
      // API returns: { success: true, data: { items: [], pagination: {} }, meta: {} }
      const adminData = response.data.data;
      setAdmins(adminData.items || []);
      setTotalRows(adminData.pagination?.total || 0);
    } catch (error) {
      console.error('Failed to fetch admins:', error);
      // Set empty array on error to prevent undefined
      setAdmins([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [filters, paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const openPasswordDialog = (admin: User) => {
    setSelectedAdmin(admin);
    setNewPassword('');
    setPasswordError(null);
    setPasswordDialogOpen(true);
  };

  const closePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setSelectedAdmin(null);
    setNewPassword('');
    setPasswordError(null);
    setPasswordLoading(false);
    setShowPassword(false);
  };

  const handleSetPassword = async () => {
    if (!selectedAdmin || !newPassword) return;
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }
    setPasswordLoading(true);
    setPasswordError(null);
    try {
      await api.patch(`/admins/${selectedAdmin.id}/password`, { password: newPassword });
      closePasswordDialog();
      fetchAdmins(); // Refresh the list
    } catch (error: unknown) {
      console.error('Failed to set password:', error);
      let errorMessage = 'Failed to set password';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { error?: { message?: string } } } };
        errorMessage = axiosError.response?.data?.error?.message || errorMessage;
      }
      setPasswordError(errorMessage);
    } finally {
      setPasswordLoading(false);
    }
  };

  const openDeleteDialog = (admin: User) => {
    setAdminToDelete(admin);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setAdminToDelete(null);
    setDeleteLoading(false);
  };

  const handleDeleteAdmin = async () => {
    if (!adminToDelete) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/admins/${adminToDelete.id}`);
      closeDeleteDialog();
      fetchAdmins(); // Refresh the list
    } catch (error: unknown) {
      console.error('Failed to delete admin:', error);
      // You might want to show an error message here
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 120 },
    { field: 'email', headerName: 'Email', flex: 1.5, minWidth: 150 },
    { field: 'role', headerName: 'Role', flex: 0.5, minWidth: 80 },
    { field: 'status', headerName: 'Status', flex: 0.5, minWidth: 80 },
    {
      field: 'lastLoginAt',
      headerName: 'Last Login',
      flex: 1,
      minWidth: 120,
      valueFormatter: (params) =>
        params.value ? new Date(params.value).toLocaleString() : 'Never',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title="Edit Admin">
            <IconButton
              size="small"
              onClick={() => navigate(`/admins/${params.row.id}/edit`)}
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={params.row.status === 'Active' ? 'Deactivate Admin' : 'Activate Admin'}>
            <IconButton
              size="small"
              onClick={() => handleStatusToggle(params.row)}
              color={params.row.status === 'Active' ? 'error' : 'success'}
            >
              {params.row.status === 'Active' ? (
                <CancelIcon fontSize="small" />
              ) : (
                <CheckCircleIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          {currentUser?.role === 'super_admin' && (
            <>
              <Tooltip title="Set Password">
                <IconButton
                  size="small"
                  onClick={() => openPasswordDialog(params.row)}
                  color="secondary"
                >
                  <LockIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete Admin">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => openDeleteDialog(params.row)}
                    disabled={params.row.role === 'super_admin'}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  const handleStatusToggle = async (admin: User) => {
    try {
      await api.patch(`/admins/${admin.id}/status`, {
        status: admin.status === 'Active' ? 'Inactive' : 'Active',
      });
      fetchAdmins();
    } catch (error) {
      console.error('Failed to update admin status:', error);
    }
  };

  return (
    <Box sx={{ overflow: 'auto' }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h4">Manage Admins</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/admins/new')}
        >
          Add New Admin
        </Button>
      </Box>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Search"
                value={filters.search || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setFilters((prev) => ({ ...prev, search: value }));
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status || ''}
                  label="Status"
                  onChange={(e) => {
                    const value = e.target.value as Status | '';
                    setFilters((prev) => ({
                      ...prev,
                      status: value || undefined,
                    }));
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={filters.role || ''}
                  label="Role"
                  onChange={(e) => {
                    const value = e.target.value as Role | '';
                    setFilters((prev) => ({
                      ...prev,
                      role: value || undefined,
                    }));
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="super_admin">Super Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={admins}
          columns={columns}
          pagination
          paginationMode="server"
          rowCount={totalRows}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          loading={loading}
          disableRowSelectionOnClick
          sx={{
            width: '100%',
            '& .MuiDataGrid-root': {
              width: '100%',
            },
          }}
        />
      </Box>

      {/* Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={closePasswordDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Set Password for {selectedAdmin?.name}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            variant="outlined"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            error={!!passwordError}
            helperText={passwordError}
            disabled={passwordLoading}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  disabled={passwordLoading}
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closePasswordDialog} disabled={passwordLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSetPassword}
            variant="contained"
            disabled={passwordLoading || !newPassword}
          >
            {passwordLoading ? <CircularProgress size={20} /> : 'Set Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Admin</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the admin "{adminToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAdmin}
            variant="contained"
            color="error"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminsPage;